import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, CheckCircle2, Loader2, Monitor, ShieldCheck } from "lucide-react";

/** Celular já logado: libera a entrada num computador (QR lido pela câmera ou código de 6 números). */
export default function EntrarComputador() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [qs] = useSearchParams();
  const [pedido, setPedido] = useState(qs.get("p") || "");
  const [info, setInfo] = useState(null);
  const [codigo, setCodigo] = useState("");
  const [estado, setEstado] = useState(qs.get("p") ? "carregando" : "codigo");   // codigo | carregando | confirmar | liberado | recusado | erro
  const [erro, setErro] = useState("");
  const [busy, setBusy] = useState(false);
  const inicio = user?.role === "admin" ? "/admin" : user?.role === "instructor" ? "/prof" : "/app";

  useEffect(() => {
    if (!user || !qs.get("p")) return;
    api.get(`/auth/qr/${encodeURIComponent(qs.get("p"))}`)
      .then((r) => { setInfo(r.data); setEstado("confirmar"); })
      .catch((e) => { setErro(formatApiError(e.response?.data?.detail)); setEstado("erro"); });
  }, [user, qs]);

  const buscarCodigo = async (e) => {
    e.preventDefault(); setBusy(true); setErro("");
    try { const r = await api.post("/auth/qr/codigo", { codigo }); setPedido(r.data.pedido); setInfo(r.data); setEstado("confirmar"); }
    catch (err) { setErro(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const responder = async (liberar) => {
    setBusy(true); setErro("");
    try { await api.post(`/auth/qr/${encodeURIComponent(pedido)}/${liberar ? "liberar" : "recusar"}`); setEstado(liberar ? "liberado" : "recusado"); }
    catch (err) { setErro(formatApiError(err.response?.data?.detail)); setEstado("erro"); }
    finally { setBusy(false); }
  };
  const outroCodigo = () => { setPedido(""); setInfo(null); setCodigo(""); setErro(""); setEstado("codigo"); navigate("/entrar-computador", { replace: true }); };

  let corpo;
  if (user === null) corpo = <div className="grid h-40 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-slate-400" /></div>;
  else if (!user) corpo = (
    <>
      <h1 className="font-display text-2xl font-bold text-slate-900">Entre primeiro neste celular</h1>
      <p className="mt-2 text-sm text-slate-600">Para liberar o computador, este celular precisa estar logado na sua conta. Entre com seu e-mail e senha e você volta direto para cá.</p>
      <Button asChild className="mt-5 h-11 w-full text-white" style={{ background: "#232D4F" }}>
        <Link to={`/login?next=computador${qs.get("p") ? `&p=${encodeURIComponent(qs.get("p"))}` : ""}`}>Entrar neste celular</Link>
      </Button>
    </>
  );
  else if (user.onboarding_required) corpo = (
    <>
      <h1 className="font-display text-2xl font-bold text-slate-900">Conclua seu primeiro acesso</h1>
      <p className="mt-2 text-sm text-slate-600">Antes de liberar outro aparelho, termine o primeiro acesso aqui no celular.</p>
      <Button asChild className="mt-5 h-11 w-full text-white" style={{ background: "#232D4F" }}><Link to="/primeiro-acesso">Continuar</Link></Button>
    </>
  );
  else if (estado === "carregando") corpo = <div className="grid h-40 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-slate-400" /></div>;
  else if (estado === "codigo") corpo = (
    <form onSubmit={buscarCodigo}>
      <h1 className="font-display text-2xl font-bold text-slate-900">Entrar no computador</h1>
      <p className="mt-2 text-sm text-slate-600">No computador, abra <strong>iepplataforma.com</strong>, clique em <strong>Entrar com o celular</strong> e digite aqui o código de 6 números que aparece na tela.</p>
      <Input value={codigo} onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="000000"
        className="mt-5 h-14 text-center font-mono text-3xl tracking-[0.3em]" data-testid="qr-codigo-input" autoFocus />
      {erro && <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{erro}</p>}
      <Button type="submit" disabled={busy || codigo.length !== 6} className="mt-4 h-11 w-full text-white" style={{ background: "#232D4F" }} data-testid="qr-codigo-continuar">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continuar"}
      </Button>
      <p className="mt-4 text-xs text-slate-500">Dica: dá também para apontar a câmera do celular para o QR code da tela do computador.</p>
    </form>
  );
  else if (estado === "confirmar" && info) corpo = (
    <>
      <h1 className="font-display text-2xl font-bold text-slate-900">Liberar este computador?</h1>
      <p className="mt-2 text-sm text-slate-600">Ele vai entrar na sua conta, <strong>{user.name?.split(" ")[0]}</strong>, sem pedir senha.</p>
      <div className="mt-4 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="qr-aparelho">
        <Monitor className="h-8 w-8 shrink-0 text-[#232D4F]" />
        <div className="text-sm">
          <div className="font-semibold text-slate-900">{info.device || "Computador"} · {info.browser}</div>
          <div className="text-slate-500">{info.city ? `${info.city}${info.region ? `/${info.region}` : ""} (aproximado)` : "Local não identificado"}{info.mesma_rede ? " · mesma internet deste celular" : ""}</div>
        </div>
      </div>
      {info.longe && (
        <p className="mt-3 flex gap-2 rounded-lg bg-red-50 px-3 py-2.5 text-sm text-red-800" data-testid="qr-longe"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> Este computador parece estar em <strong>outra cidade</strong>. Só libere se ele estiver na sua frente agora.</p>
      )}
      <p className="mt-3 flex gap-2 text-xs text-slate-500"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#1E9E2A]" /> Só libere se foi você quem abriu o QR code no computador. Ninguém do IEP pede para você ler um QR code.</p>
      <Button onClick={() => responder(true)} disabled={busy} className="mt-5 h-12 w-full text-base font-semibold text-white" style={{ background: "#1E9E2A" }} data-testid="qr-liberar">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Liberar este computador"}
      </Button>
      <Button onClick={() => responder(false)} disabled={busy} variant="outline" className="mt-2 h-11 w-full" data-testid="qr-recusar">Não fui eu · Cancelar</Button>
    </>
  );
  else if (estado === "liberado") corpo = (
    <div className="text-center">
      <CheckCircle2 className="mx-auto h-14 w-14 text-[#1E9E2A]" />
      <h1 className="mt-3 font-display text-2xl font-bold text-slate-900">Pronto!</h1>
      <p className="mt-2 text-sm text-slate-600">O computador já está entrando na sua conta. Pode olhar para a tela dele.</p>
      <p className="mt-3 text-xs text-slate-500">Para desconectar depois: Minha conta → Aparelhos conectados.</p>
      <Button asChild variant="outline" className="mt-5 h-11 w-full"><Link to={inicio}>Voltar para a plataforma</Link></Button>
    </div>
  );
  else corpo = (
    <div className="text-center">
      <p className="text-sm text-slate-700">{estado === "recusado" ? "Tudo certo: o computador não entrou na sua conta." : erro}</p>
      <Button onClick={outroCodigo} className="mt-5 h-11 w-full text-white" style={{ background: "#232D4F" }}>Digitar um código</Button>
      <Button asChild variant="outline" className="mt-2 h-11 w-full"><Link to={inicio}>Voltar para a plataforma</Link></Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#EEF2F6] p-3 sm:p-6">
      <div className="mx-auto max-w-md rounded-3xl bg-white p-6 shadow-xl ring-1 ring-black/5 sm:p-8 animate-fade-up">
        <Brand size="md" className="mb-6" />
        {corpo}
      </div>
    </div>
  );
}
